import React, { useState, useEffect } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { MdInfoOutline, MdOutlineDeleteOutline } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addMapIndicator, removeMapIndicator } from "../../store/workspaceSlice";
import axios from "axios";
import { BASE_URL } from "../../commonConfig/ApiConfig";
import SafariModalWrapper from "./safariwapper";

const LocationFilter = ({
    show,
    onClose,
}) => {
    const dispatch = useDispatch();
    const selectedMapFeatures = useSelector(state => state.workspace.selectedMapFeatures);
    
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedListType, setSelectedListType] = useState("workspace");
    const [drillStack, setDrillStack] = useState([
        { label: "India", value: 1000, geo_level: 1 },
    ]);
    const [currentList, setCurrentList] = useState([]);

    // Fetch locations when drilling down
    useEffect(() => {
        const load = async () => {
            const top = drillStack[drillStack.length - 1];
            const parentCode = top.value === null ? 1000 : top.value;
            const nextLevel = top.geo_level;
            try {
                const res = await axios.post(`${BASE_URL}locations/location-details`, {
                    lgd_code: parentCode,
                    geo_level_code: nextLevel,
                });
                const children = res.data.locations || [];
                const mapped = (children || []).map((loc) => ({
                    name: loc.name || loc.label,
                    lgd_code: loc.code || loc.value || loc.lgd_code || loc.properties?.code,
                    geo_level: loc.geo_level || loc.geo_level_code || loc.properties?.geo_level || nextLevel,
                    geometry: loc.geometry,
                    properties: loc.properties || "",
                }));
                setCurrentList(mapped);
            } catch (err) {
                console.error("Error fetching locations:", err);
                setCurrentList([]);
            }
        };
        if (selectedListType === "all") load();
    }, [drillStack, selectedListType]);

    const handleAddLocation = (loc) => {
        dispatch(addMapIndicator({
            id: loc.lgd_code || loc.name,
            name: loc.name,
            geo_level_code: loc.geo_level,
            geometry: loc.geometry,
            properties: loc.properties || "",
            sourceType: 'mapPopup',
            addedAt: new Date().toISOString()
        }));
    };
    
    const handleRemoveLocation = (locationId) => {
        dispatch(removeMapIndicator(locationId));
    };

    const getWorkspaceList = () => {
        // Create a list with India as the first item
        const indiaItem = {
            name: "India",
            lgd_code: 1000,
            geo_level: 1,
            geometry: null,
            properties: { name: "India", geo_level_code: 1, lgd_code: 1000 },
            isDefault: true
        };
        
        // Map the selected features and add them to the list
        const locationsList = selectedMapFeatures.map(loc => ({
            name: loc.name || loc.properties?.name,
            lgd_code: loc.id,
            geo_level: loc.geo_level_code || loc.properties?.geo_level_code,
            geometry: loc.geometry,
            properties: loc.properties || "",
        }));
        
        // Check if India is already in the list to avoid duplication
        const hasIndia = locationsList.some(loc => 
            loc.name === "India" || loc.lgd_code === 1000
        );
        
        // Return the combined list with India first if not already present
        return hasIndia ? locationsList : [indiaItem, ...locationsList];
    };

    const filterBySearch = (arr) =>
        arr.filter((x) =>
            x.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const getDisplayed = () => {
        if (selectedListType === "workspace") return filterBySearch(getWorkspaceList());
        if (selectedListType === "all") return filterBySearch(currentList);
        return [];
    };

    const displayed = getDisplayed();

    const goDrillDown = (loc) => {
        setDrillStack((prev) => [
            ...prev,
            { label: loc.name || loc.label, value: loc.lgd_code, geo_level: loc.geo_level },
        ]);
    };

    const goBackUp = () => {
        if (drillStack.length > 1)
            setDrillStack((prev) => prev.slice(0, -1));
    };

    return (
  <SafariModalWrapper show={show} onClose={onClose}>
                    <button className="btn-close position-absolute top-0 end-0 m-3" onClick={onClose} />

                    <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                        Add Locations <OverlayTrigger
                            placement="right"
                            overlay={
                                <Tooltip id="filter-tooltip">
                                    <ol className="mb-0 ps-3">
                                        <li>Add with "+", locations will be added to workspace.</li>
                                        <li>"Show All Children" expands deeper levels (geo level &lt; 4).</li>
                                        <li>Already added items are disabled.</li>
                                    </ol>
                                </Tooltip>
                            }
                        >
                            <span>
                                <MdInfoOutline style={{ cursor: "pointer", color: "#000000" }} />
                            </span>
                        </OverlayTrigger>
                    </h5>

                    <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                        {/* Tabs on the left */}
                        <div className="btn-group pt-2" role="group" aria-label="Filter tabs">
                            <input
                                type="radio"
                                className="btn-check"
                                name="filter-tabs"
                                id="workspace-tab"
                                autoComplete="off"
                                checked={selectedListType === "workspace"}
                                onChange={() => setSelectedListType("workspace")}
                            />
                            <label className="btn btn-outline-primary" htmlFor="workspace-tab">
                                Workspace
                            </label>

                            <input
                                type="radio"
                                className="btn-check"
                                name="filter-tabs"
                                id="all-tab"
                                autoComplete="off"
                                checked={selectedListType === "all"}
                                onChange={() => setSelectedListType("all")}
                            />
                            <label className="btn btn-outline-primary" htmlFor="all-tab">
                                All Locations
                            </label>
                        </div>

                        {/* Close button on the right */}
                        <div>
                            <button className="btn btn-primary" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </div>

                    {selectedListType === "all" && drillStack.length > 1 && (
                        <div className="mb-2">
                            <button className="btn btn-primary" onClick={goBackUp}>
                                Back
                            </button>
                            <span className="ms-2 fw-semibold">
                                {drillStack.map((d) => d.label).join(" > ")}
                            </span>
                        </div>
                    )}

                    <input
                        type="text"
                        placeholder="Search"
                        className="form-control mb-3"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <ul className="list-group">
                        {displayed.length === 0 ? (
                            <li className="list-group-item text-muted">No locations</li>
                        ) : (
                            displayed.map((item, index) => {
                                const isAlreadyAdded = selectedMapFeatures.some(
                                    (sel) => `${sel.lgd_code}-${sel.geo_level}` === `${item.lgd_code}-${item.geo_level}`
                                );

                                const listItemStyle = {
                                    backgroundColor: isAlreadyAdded ? "#f2f2f2" : "inherit",
                                    color: isAlreadyAdded ? "#2d7be5" : "inherit",
                                };

                                return (
                                    <li
                                        key={index}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                        style={listItemStyle}
                                    >
                                        <span>
                                            {index + 1}. {item.name}
                                        </span>

                                        <div className="d-flex align-items-center gap-2">
                                            {selectedListType === "all" && item.geo_level < 4 && (
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => goDrillDown(item)}
                                                >
                                                    {item.geo_level === 1 && "All States"}
                                                    {item.geo_level === 2 && "All Districts"}
                                                    {item.geo_level === 3 && "All ULBs"}
                                                </button>
                                            )}

                                            {selectedListType === "workspace" ? (
                                                <button
                                                    onClick={() => handleRemoveLocation(item.name || item.value)}
                                                    className="btn btn-sm btn-outline-danger border-0"
                                                    title="Remove location"
                                                    disabled={item.isDefault}
                                                >
                                                    <MdOutlineDeleteOutline size={18} />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleAddLocation(item)}
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={isAlreadyAdded}
                                                >
                                                    +
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>
  </SafariModalWrapper>
);

};

export default LocationFilter;